import { i18n } from '@investigator/shared/localization';

const { url: urlValidation } = i18n.forms.validation;

/**
 * Helper function to check if a hostname is an IP address (IPv4 or IPv6)
 * @param hostname - The hostname to check
 * @returns true if hostname is an IP address
 * Note: Since URL constructor already validated the URL, we just need to detect IP format
 */
const isIPAddress = (hostname: string): boolean | string => {
  // IPv4 pattern: matches x.x.x.x format
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;

  // URL constructor already validated it, so we just detect the pattern
  // Also supports IPv4-mapped IPv6 addresses like [::ffff:192.0.2.1]
  const ipv6Pattern = /^(\[)?[0-9a-fA-F:.]+(\])?$/;

  if (ipv4Pattern.test(hostname)) {
    // Validate IPv4 octets are in range 0-255
    const isValidOctets = hostname.split('.').every((octet) => {
      const num = parseInt(octet, 10);
      return num >= 0 && num <= 255;
    });
    if (!isValidOctets) {
      return urlValidation.invalid;
    }
    return true;
  }

  // Check for IPv6: remove brackets and check for colon-separated hex
  const hostnameWithoutBrackets = hostname.replace(/^\[|\]$/g, '');

  // IPv6 addresses contain colons and hex characters
  if (hostnameWithoutBrackets.includes(':') && ipv6Pattern.test(hostname)) {
    return true;
  }

  return false;
};

/**
 * TLD validation to catch common user typos and ensure TLD presence
 * The URL constructor handles most validation, we just need to catch specific typos and missing TLDs
 * Accepts both domain names and IP addresses (IPv4 and IPv6)
 * @param value - The value to validate
 * @returns true if valid, error message string if TLD typo detected or missing
 */
export const validateExporterUrl = (value: string): string | true => {
  try {
    // must have protocol (http or https)
    if (!/^https?:\/\//i.test(value)) {
      return urlValidation.missingProtocol;
    }

    // URL constructor handles most validation (malformed URLs, invalid ports, etc.)
    const url = new URL(value);
    const { hostname } = url;

    // If hostname is an IP address, bypass domain validation
    const isIP = isIPAddress(hostname);
    if (isIP) {
      return typeof isIP === 'string' ? isIP : true;
    }

    // For domain names, check if hostname has a TLD (must have at least one dot followed by letters)
    if (!hostname.match(/\.[a-zA-Z]{2,}$/)) {
      return urlValidation.missingTLD;
    }

    // Check for repeated letters anywhere in the TLD (e.g., .commmmm, .neeeet, .orggg, .eduuu, .zzzz, .aaa)
    const tldMatch = hostname.match(/\.([a-zA-Z]+)$/);
    if (tldMatch) {
      const tld = tldMatch[1];
      if (tld.match(/([a-zA-Z])\1{2,}/)) {
        return urlValidation.tldTypo;
      }
    }

    return true;
  } catch (err: unknown) {
    // URL constructor catches: malformed URLs, invalid ports, missing hostnames, etc.
    if (err instanceof TypeError || (err as Error)?.name === 'TypeError') {
      return urlValidation.invalid;
    }
    // Perhaps we can log to a cloud service in the future once we implement it
    return urlValidation.unexpectedError;
  }
};
